import { useEffect, useState } from "react";
import { Button } from "ui/button";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";

interface Props {
  original: File;
  converted: File;
  onAccept: () => void;
  onReupload: () => void;
}

export function PhotoNormalizationDialog({
  original,
  converted,
  onAccept,
  onReupload,
}: Props) {
  const [urls, setUrls] = useState<{ original: string; converted: string }>();
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const next = {
      original: URL.createObjectURL(original),
      converted: URL.createObjectURL(converted),
    };
    setUrls(next);
    setLoaded(false);
    setFailed(false);
    return () => {
      URL.revokeObjectURL(next.original);
      URL.revokeObjectURL(next.converted);
    };
  }, [original, converted]);
  
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onReupload();
      }}
    >
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle>Review Optimized Photo</DialogTitle>
          <DialogDescription>
            Compare your original photo with the optimized photo before
            accepting.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-2 font-medium">Original Photo</p>
            {urls && (
              <img
                src={urls.original}
                alt="Original uploaded portrait"
                className="h-72 w-full rounded border object-contain"
              />
            )}
          </div>
          <div>
            <p className="mb-2 font-medium">Optimized Photo</p>
            {urls && (
              <img
                src={urls.converted}
                alt="Optimized portrait"
                className="h-72 w-full rounded border object-contain"
                onLoad={() => setLoaded(true)}
                onError={() => {
                  setFailed(true);
                  toast.error(
                    "The optimized image could not be displayed. Please reupload.",
                  );
                }}
              />
            )}
          </div>
        </div>
        {failed && (
          <p role="alert" className="text-red-600">
            The optimized image could not be displayed. Please reupload.
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onReupload}>
            Re-Upload
          </Button>
          <Button type="button" disabled={!loaded || failed} onClick={onAccept}>
            Accept
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
